import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, consumeAuthNotice, setAccessToken } from '@/api/client';
import BrandMark from './BrandMark';
import BrandTagline from './BrandTagline';
import BrandWord from './BrandWord';
import { mapLoginApiError } from '@/utils/loginErrorMapping';
import { hasLoginFieldErrors, validateLoginFields, type LoginFieldErrors } from '@/utils/loginValidation';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite')?.trim() || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({});
  const [formError, setFormError] = useState('');
  const [accountNotice] = useState(() => consumeAuthNotice());
  const [loading, setLoading] = useState(false);

  function clearEmailError() {
    setFieldErrors((prev) => {
      if (!prev.email) return prev;
      const next = { ...prev };
      delete next.email;
      return next;
    });
    setFormError('');
  }

  function clearPasswordError() {
    setFieldErrors((prev) => {
      if (!prev.password) return prev;
      const next = { ...prev };
      delete next.password;
      return next;
    });
    setFormError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;

    const nextFieldErrors = validateLoginFields(email, password);
    setFieldErrors(nextFieldErrors);
    setFormError('');
    if (hasLoginFieldErrors(nextFieldErrors)) {
      return;
    }

    setLoading(true);
    try {
      const result = await api.auth.login(email.trim(), password);
      setFieldErrors({});
      setFormError('');
      setAccessToken(result.accessToken);
      navigate(inviteToken ? `/invite/${encodeURIComponent(inviteToken)}` : '/', { replace: true });
    } catch (err) {
      const mapped = mapLoginApiError(err);
      setFormError(mapped.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden />
      <div className="login-panel">
        <div className="login-brand">
          <BrandMark size="large" />
          <h1><BrandWord /></h1>
          <BrandTagline className="brand-tag login-brand-tag" />
          <p className="muted">Sign in with your account</p>
          {inviteToken ? (
            <p className="muted login-invite-hint">Sign in to accept your organization invitation.</p>
          ) : null}
        </div>

        <form className="login-card card card-elevated" onSubmit={handleSubmit} noValidate>
          <h2>Sign in</h2>
          {accountNotice ? (
            <div className="alert alert-error" role="alert">
              {accountNotice}
            </div>
          ) : null}
          {formError ? (
            <div id="login-form-error" className="alert alert-error" role="alert">
              {formError}
            </div>
          ) : null}

          <label htmlFor="login-email">
            Email
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearEmailError();
              }}
              placeholder="admin@example.com"
              autoFocus
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
              disabled={loading}
            />
          </label>
          {fieldErrors.email ? (
            <p id="login-email-error" className="app-form-field__error" role="alert">
              {fieldErrors.email}
            </p>
          ) : null}

          <label htmlFor="login-password">
            Password
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearPasswordError();
              }}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
              disabled={loading}
            />
          </label>
          {fieldErrors.password ? (
            <p id="login-password-error" className="app-form-field__error" role="alert">
              {fieldErrors.password}
            </p>
          ) : null}

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="muted login-footer">
            No account? <Link to="/register">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
